"use client";

import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/utils/supabase/client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { FileText, Plus, Edit, Trash2, Pen, Download, DollarSign } from "lucide-react";
import { InvoicePDFGenerator } from "@/components/InvoicePDFGenerator";
import { EmailSender } from "@/components/EmailSender";
import { SignaturePad } from "@/components/SignaturePad";
import { showSuccess, showError } from "@/utils/toast";
import { format } from "date-fns";
import { generateInvoiceFromRecentBillableTime } from "@/utils/invoicing";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useNavigate } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface Invoice {
  id: string;
  client_id: string;
  invoice_number: string;
  total_amount: number;
  status: 'draft' | 'sent' | 'paid';
  due_date: string;
  created_at: string;
  clients: { name: string; email: string };
  signed_at?: string;
  signature_url?: string;
  signer_id?: string;
  consent_given?: boolean;
}

const InvoicesPage = () => {
  const { profile, company, getFeatureAccess } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState<Invoice | null>(null);
  const [showSignature, setShowSignature] = useState(false);
  const [invoiceToSign, setInvoiceToSign] = useState<Invoice | null>(null);
  const [selectedClientId, setSelectedClientId] = useState<string>("");

  // NEW: Redirect client-role users to Client Portal
  useEffect(() => {
    if (profile?.role === 'client') {
      navigate('/client-portal', { replace: true });
    }
  }, [profile, navigate]);

  // Hide invoice management for client-role users
  const canManageInvoices = profile?.role !== 'client';

  // Check if this is IBP consulting tenant
  const isIBPConsulting = getFeatureAccess('ibp_consulting');

  // Seed client select on edit/new
  useEffect(() => {
    setSelectedClientId(editingInvoice?.client_id || "");
  }, [editingInvoice, showForm]);

  const { data: invoices, isLoading: invoicesLoading, error: invoicesError } = useQuery<Invoice[]>({
    queryKey: ["invoices", company?.id],
    queryFn: async () => {
      if (!company?.id) return [];
      const { data, error } = await supabase
        .from("invoices")
        .select("*, clients(name, email)")
        .eq("company_id", company.id)
        .order("created_at", { ascending: false });
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!company?.id && canManageInvoices,
    retry: (failureCount, error) => {
      if (error.message.includes('permission')) return false;
      return failureCount < 3;
    },
  });

  const { data: billableTime, isLoading: timeLoading } = useQuery({
    queryKey: ["billable_time", company?.id],
    queryFn: async () => {
      if (!company?.id) return [];
      const { data, error } = await supabase
        .from("time_logs")
        .select("*, clients(name)")
        .eq("company_id", company.id)
        .eq("billable", true)
        .order("start_time", { ascending: false });
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!company?.id && canManageInvoices,
  });

  const { data: clients, isLoading: clientsLoading } = useQuery({
    queryKey: ["clients", company?.id],
    queryFn: async () => {
      if (!company?.id) return [];
      const { data, error } = await supabase
        .from("clients")
        .select("id, name")
        .eq("company_id", company.id)
        .order("name");
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!company?.id,
  });

  const createMutation = useMutation({
    mutationFn: async (invoiceData: any) => {
      const { data, error } = await supabase
        .from("invoices")
        .insert({ ...invoiceData, company_id: company?.id })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoices"] });
      setShowForm(false);
      showSuccess(isIBPConsulting ? "Invoice created for client!" : "Invoice created successfully!");
    },
    onError: () => showError(isIBPConsulting ? "Failed to create invoice for client" : "Failed to create invoice"),
  });

  const updateMutation = useMutation({
    mutationFn: async (invoiceData: any) => {
      const { data, error } = await supabase
        .from("invoices")
        .update(invoiceData)
        .eq("id", invoiceData.id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoices"] });
      setEditingInvoice(null);
      setShowForm(false);
      showSuccess(isIBPConsulting ? "Invoice updated for client!" : "Invoice updated successfully!");
    },
    onError: () => showError(isIBPConsulting ? "Failed to update invoice for client" : "Failed to update invoice"),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("invoices").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoices"] });
      showSuccess(isIBPConsulting ? "Invoice removed from client records" : "Invoice deleted!");
    },
    onError: () => showError(isIBPConsulting ? "Failed to remove invoice from client records" : "Failed to delete invoice"),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget as HTMLFormElement);
    const data = {
      client_id: formData.get("client_id") as string,
      invoice_number: formData.get("invoice_number") as string,
      total_amount: parseFloat(formData.get("total_amount") as string),
      status: formData.get("status") as 'draft' | 'sent' | 'paid',
      due_date: formData.get("due_date") as string,
      ...(editingInvoice && { id: editingInvoice.id }),
    };
    if (editingInvoice) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  const handleSignInvoice = (invoice: Invoice) => {
    setInvoiceToSign(invoice);
    setShowSignature(true);
  };

  const handleGenerateFromTime = async () => {
    if (!company?.id) return;
    const invoice = await generateInvoiceFromRecentBillableTime(company.id);
    if (!invoice) {
      showSuccess(isIBPConsulting ? "No recent billable time found for client. Log some time first!" : "No recent billable time found. Log some time first!");
      return;
    }
    showSuccess(isIBPConsulting ? `Invoice #${invoice.invoice_number} created from client billable time!` : `Invoice #${invoice.invoice_number} created from billable time!`);
    queryClient.invalidateQueries({ queryKey: ["invoices"] });
  };

  if (invoicesLoading) {
    return (
      <div className="container mx-auto p-4">
        <div className="flex justify-between items-center mb-6">
          <Skeleton className="h-8 w-32" />
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {[1, 2].map(i => (
            <Card key={i}>
              <CardContent className="p-6">
                <Skeleton className="h-4 w-48 mb-4" />
                <Skeleton className="h-20 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
        <Card>
          <CardContent className="p-6">
            <Skeleton className="h-4 w-full mb-2" />
            <Skeleton className="h-4 w-full mb-2" />
            <Skeleton className="h-4 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (invoicesError) {
    return (
      <div className="container mx-auto p-4">
        <Alert variant="destructive">
          <AlertDescription>
            Failed to load invoices. {invoicesError.message.includes('permission') ? 'You may not have permission to view invoices.' : 'Please try again later.'}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold flex items-center space-x-2">
          <FileText className="h-8 w-8" />
          <span>{isIBPConsulting ? "Client Invoices" : "Invoices"}</span>
        </h1>
        {canManageInvoices && (
          <Dialog open={showForm} onOpenChange={setShowForm}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                {isIBPConsulting ? "New Client Invoice" : "New Invoice"}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>{editingInvoice ? (isIBPConsulting ? "Edit Client Invoice" : "Edit Invoice") : (isIBPConsulting ? "New Client Invoice" : "New Invoice")}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Hidden field so FormData includes the client_id */}
                <input type="hidden" name="client_id" value={selectedClientId} />
                <div className="space-y-2">
                  <label htmlFor="client_id">{isIBPConsulting ? "Client" : "Customer"}</label>
                  <Select value={selectedClientId} onValueChange={setSelectedClientId}>
                    <SelectTrigger>
                      <SelectValue placeholder={isIBPConsulting ? "Select Client" : "Select Customer"} />
                    </SelectTrigger>
                    <SelectContent>
                      {clients?.map((c: any) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label htmlFor="invoice_number">{isIBPConsulting ? "Invoice #" : "Invoice Number"}</label>
                  <Input id="invoice_number" name="invoice_number" defaultValue={editingInvoice?.invoice_number || `INV-${Date.now()}`} required />
                </div>
                <div className="space-y-2">
                  <label htmlFor="total_amount">{isIBPConsulting ? "Total Amount (£)" : "Total Amount ($)"}</label>
                  <Input id="total_amount" name="total_amount" type="number" step="0.01" defaultValue={editingInvoice?.total_amount} required />
                </div>
                <div className="space-y-2">
                  <label htmlFor="status">Status</label>
                  <select id="status" name="status" defaultValue={editingInvoice?.status || "draft"} className="w-full p-2 border rounded">
                    <option value="draft">Draft</option>
                    <option value="sent">Sent</option>
                    <option value="paid">Paid</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <label htmlFor="due_date">Due Date</label>
                  <Input id="due_date" name="due_date" type="date" defaultValue={editingInvoice?.due_date} required />
                </div>
                <Button type="submit" className="w-full">
                  {editingInvoice ? "Update" : "Create"} {isIBPConsulting ? "Invoice" : "Invoice"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Dialog open={showSignature} onOpenChange={setShowSignature}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Sign Invoice #{invoiceToSign?.invoice_number}</DialogTitle>
          </DialogHeader>
          {invoiceToSign && (
            <SignaturePad
              documentData={invoiceToSign}
              documentType="invoice"
              tableName="invoices"
              onSignComplete={() => {
                setShowSignature(false);
                queryClient.invalidateQueries({ queryKey: ["invoices"] });
                showSuccess(isIBPConsulting ? "Client invoice signed successfully!" : "Invoice signed successfully!");
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Card>
          <CardHeader>
            <CardTitle>{isIBPConsulting ? "Recent Client Billable Time" : "Recent Billable Time"}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {billableTime?.slice(0, 5).map((log: any) => (
                <div key={log.id} className="flex justify-between text-sm">
                  <span>{log.description} - {log.clients?.name}</span>
                  <Badge>{Math.round(log.duration_minutes / 60)}h</Badge>
                </div>
              ))}
            </div>
            <Button variant="outline" size="sm" className="mt-2" onClick={handleGenerateFromTime} disabled={(billableTime?.length || 0) === 0}>
              <DollarSign className="mr-2 h-4 w-4" />
              {isIBPConsulting ? "Generate Client Invoice from Time" : "Generate Invoice from Time"}
            </Button>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>{isIBPConsulting ? "Client Invoice Summary" : "Summary"}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span>{isIBPConsulting ? "Total Client Invoices" : "Total Invoices"}</span>
                <span className="font-bold">{invoices?.length || 0}</span>
              </div>
              <div className="flex justify-between">
                <span>{isIBPConsulting ? "Pending Client Payments" : "Pending"}</span>
                <span className="font-bold">{invoices?.filter((i: Invoice) => i.status === 'sent').length || 0}</span>
              </div>
              <div className="flex justify-between">
                <span>{isIBPConsulting ? "Total Client Revenue" : "Total Revenue"}</span>
                <span className="font-bold">{isIBPConsulting ? "£" : "$"}{invoices?.reduce((sum: number, i: Invoice) => sum + i.total_amount, 0).toFixed(2) || 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      <Card>
        <CardHeader>
          <CardTitle>{isIBPConsulting ? "Client Invoices List" : "Invoices List"}</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{isIBPConsulting ? "Invoice #" : "Invoice #"}</TableHead>
                <TableHead>{isIBPConsulting ? "Client" : "Customer"}</TableHead>
                <TableHead>{isIBPConsulting ? "Amount (£)" : "Amount ($)"}</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Due Date</TableHead>
                <TableHead>Signed</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invoices?.map((invoice: Invoice) => (
                <TableRow key={invoice.id}>
                  <TableCell>{invoice.invoice_number}</TableCell>
                  <TableCell>{invoice.clients?.name || 'N/A'}</TableCell>
                  <TableCell>{isIBPConsulting ? "£" : "$"}{invoice.total_amount.toFixed(2)}</TableCell>
                  <TableCell><Badge variant={invoice.status === 'paid' ? "default" : "secondary"}>{invoice.status}</Badge></TableCell>
                  <TableCell>{format(new Date(invoice.due_date), 'MMM dd, yyyy')}</TableCell>
                  <TableCell>{invoice.consent_given ? <Badge variant="outline">Yes</Badge> : <Badge variant="secondary">No</Badge>}</TableCell>
                  <TableCell>
                    {canManageInvoices && (
                      <div className="flex flex-wrap gap-2">
                        <InvoicePDFGenerator invoiceId={invoice.id} />
                        {invoice.status === 'draft' && (
                          <EmailSender
                            to={invoice.clients?.email || ''}
                            subject={`Invoice #${invoice.invoice_number} from ${company?.name}`}
                            template="invoice"
                            invoiceId={invoice.id}
                          />
                        )}
                        {invoice.status === 'sent' && !invoice.signature_url && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleSignInvoice(invoice)}
                          >
                            <Pen className="h-4 w-4 mr-1" />
                            Sign
                          </Button>
                        )}
                        {invoice.signature_url && (
                          <Button variant="outline" size="sm" asChild>
                            <a href={invoice.signature_url} target="_blank" rel="noopener noreferrer">
                              <Download className="h-4 w-4 mr-1" />
                              Signed PDF
                            </a>
                          </Button>
                        )}
                        <Button variant="outline" size="sm" onClick={() => { setEditingInvoice(invoice); setShowForm(true); }}>
                          <Edit className="h-4 w-4 mr-1" />
                          Edit
                        </Button>
                        <Button variant="destructive" size="sm" onClick={() => deleteMutation.mutate(invoice.id)}>
                          <Trash2 className="h-4 w-4 mr-1" />
                          Delete
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {invoices?.length === 0 && <p className="text-muted-foreground text-center py-8">{isIBPConsulting ? "No client invoices yet. Create one from billable time!" : "No invoices yet. Create one from billable time!"}</p>}
        </CardContent>
      </Card>
    </div>
  );
};

export default InvoicesPage;